import React from "react";
import { Link } from "react-router-dom";
import { UploadCloud, ScanLine, Wallet, Sparkles, Zap, Trophy, Heart, Receipt, Gift, TrendingUp, ShoppingCart, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import RecentScansTicker from "../components/RecentScansTicker";
import Seo from "../components/Seo";

const ref = new URLSearchParams(window.location.search).get("ref");
if (ref) localStorage.setItem("cardark_ref", ref);

const LANDING_FAQS = [
  { q: "What is CardArk?", a: "CardArk turns a video of your trading cards into a fully identified, priced digital collection. Film yourself stacking cards (or upload photos), and the AI cuts the footage into one clean image per card, identifies each one, and prices it with real last-sold market data." },
  { q: "How does CardArk identify my cards?", a: "Vision AI reads the card name, set and number straight off the artwork in your scan and matches it to a reference image — no manual data entry. You review each card before it's filed, so nothing gets misidentified." },
  { q: "How accurate is the card pricing?", a: "Prices come from real recent sold listings, not asking prices, and are quoted at Near Mint by default. You slide a condition dial per card to recalculate the value at your card's exact grade, and every card shows its sold-price trend." },
  { q: "Does CardArk work with Pokemon and sports cards?", a: "It works best with Pokemon cards — official set data, reference images and deep price coverage — and Magic: The Gathering. Sports and other cards are supported too, with clearly-labelled estimates where live market data is thinner." },
  { q: "Is CardArk free?", a: "You can start free with monthly scan credits, and invite friends to earn more. Additional scan credits are available if you have a large collection to get through." },
];

const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: LANDING_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const STEPS = [
  { icon: UploadCloud, title: "Upload the stack", desc: "Record yourself stacking cards — or flipping through your binder, pausing on each card — or just upload photos of each card. One take, no fuss." },
  { icon: ScanLine, title: "AI can detect most cards", desc: "Frame-by-frame motion detection catches each new card as it settles — the occasional miss is one click to re-scan." },
  { icon: Sparkles, title: "Identify & catalogue", desc: "Vision AI names the card, set and rarity — filed straight into your collection, no manual data entry." },
  { icon: Wallet, title: "Know what it's worth", desc: "Real last-sold eBay data sets the Near Mint price. Slide the condition dial to recalc instantly." },
];

export default function Landing() {
  const { user, loginWithGoogle } = useAuth();

  return (
    <div className="min-h-screen bg-[#05060f] text-white overflow-x-hidden">
      <Seo
        title="CardArk — Scan, Identify & Price Your Trading Card Collection"
        description="Film your trading card stack and CardArk's AI identifies every card and prices it with real last-sold market data. Build a searchable, valued Pokemon (and more) collection in minutes."
        path="/"
        jsonLd={homeJsonLd}
      />
      <section className="keep-dark relative min-h-[92vh] flex items-center border-b border-white/10 overflow-hidden">
        <div className="glow-blob w-[500px] h-[500px] -top-32 -left-32" />
        <div className="glow-blob w-[400px] h-[400px] top-1/3 right-0" />
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1611931960487-4932667079f1?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Trading card stack"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        </div>

        <div className="relative z-10 w-full py-16">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 w-full">
            <div className="lg:col-span-7 space-y-8">
            <span data-testid="landing-eyebrow" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A78BFA] border border-[#A78BFA]/30 rounded-full px-4 py-1.5 bg-[#A78BFA]/5">
              <Zap className="w-3 h-3" /> Know exactly what you've got.
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-900 leading-[1.05] tracking-tight">
              Film your stack.<br />
              Build your <span className="bg-gradient-to-r from-white to-[#A78BFA] bg-clip-text text-transparent">collection</span>.
            </h1>
            <p className="text-base md:text-lg text-zinc-300 max-w-xl">
              Upload one video of you stacking cards — or individual photos of each card. CardArk turns them
              into clean snapshots per card, identifies each one, and prices it with real last-sold eBay data.
              Then take it further: climb the collector leaderboard, showcase your binder, list your chase
              cards and get notified when someone scans them — with a ping system to request contact and
              organise trades.
            </p>
            <p data-testid="landing-pokemon-notice" className="text-xs text-zinc-500 max-w-xl border-l-2 border-[#A78BFA]/40 pl-3 leading-relaxed">
              It works best with Pokemon cards — but other cards also work.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              {user ? (
                <Link to="/upload">
                  <Button data-testid="landing-cta-upload-button" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-8 py-6 text-base font-semibold transition-colors">
                    Scan a video <UploadCloud className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              ) : (
                <Button data-testid="landing-cta-login-button" onClick={loginWithGoogle} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-8 py-6 text-base font-semibold transition-colors">
                  Get started with Google
                </Button>
              )}
            </div>
          </div>
          </div>
          <div className="mt-16">
            <RecentScansTicker />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-4 max-w-lg">From messy shoebox to a collection you actually know, in four steps</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12">
          {STEPS.map((step, i) => (
            <div
              key={step.title}
              data-testid={`landing-step-${i}`}
              className="relative border border-white/10 rounded-2xl p-6 bg-[#111113] hover:-translate-y-1 hover:border-[#A78BFA]/30 transition-transform duration-300"
            >
              <span className="absolute top-4 right-5 font-mono-plex text-4xl font-bold text-white/5">0{i + 1}</span>
              <step.icon className="w-8 h-8 text-[#A78BFA] mb-4" />
              <h3 className="font-display font-semibold text-lg mb-2">{step.title}</h3>
              <p className="text-sm text-zinc-400">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold">Built for real collectors, not marketplaces</h2>
          <p className="text-zinc-400 text-base leading-relaxed">
            CardArk isn't just a price tag generator — it's a complete record of your collection. Every card lands
            in your Binder for manual review — your snapshot next to the AI's reference match, so nothing gets
            misfiled. Organize into named collections, filter by game type, and slide the condition dial per card
            to instantly recalculate value from Near Mint down to Damaged.
          </p>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li className="flex items-start gap-2.5">
              <TrendingUp className="w-4 h-4 text-[#A78BFA] mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Top Movers</span> — a weekly strip surfacing the biggest price jumps across your collection.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Receipt className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Sales Ledger</span> — mark a card sold and log what it went for versus market value. Your private ledger tracks total sold, profit vs. market, and every deal in one place — so you always know if you got a good price.</span>
            </li>
          </ul>
          <div className="flex flex-wrap items-center gap-3">
            {user ? (
              <Link to="/sales">
                <Button data-testid="landing-ledger-cta" variant="outline" className="rounded-full border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 px-6">
                  <Receipt className="w-4 h-4 mr-2" /> Open your Sales Ledger
                </Button>
              </Link>
            ) : (
              <Button data-testid="landing-secondary-login-button" onClick={loginWithGoogle} variant="outline" className="rounded-full border-white/20 hover:bg-white/5 px-6">
                Sign in with Google
              </Button>
            )}
          </div>
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-white/10">
          <img
            src="https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Card collection"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <section data-testid="landing-showcase-section" className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="relative order-last md:order-first">
          <div className="rounded-2xl border border-white/10 bg-[#111113] p-5 max-w-md mx-auto">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
              <span className="w-2 h-2 rounded-full bg-emerald-400" /> Live showcase link
            </div>
            <div className="rounded-xl border-l-4 border-[#7C3AED] bg-black/40 p-4">
              <p className="text-[10px] text-zinc-500 mb-1">CardArk</p>
              <p className="text-sm font-semibold text-white">Alex's Card Binder — CardArk Showcase</p>
              <p className="text-xs text-zinc-400 mt-1">Browse 128 trading cards scanned, identified and priced with real last-sold data.</p>
              <div className="mt-3 flex gap-2">
                {["from-[#7C3AED] to-[#4C1D95]", "from-[#F59E0B] to-[#92400E]", "from-[#10B981] to-[#065F46]", "from-[#3B82F6] to-[#1E3A8A]"].map((g, i) => (
                  <div key={i} className={`h-20 w-14 rounded-md bg-gradient-to-br ${g} border border-white/10`} />
                ))}
              </div>
            </div>
            <p className="text-[10px] text-zinc-600 mt-3">↑ what friends see when you paste your link in Discord or X</p>
          </div>
        </div>
        <div className="space-y-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold">Show it off with a Showcase link</h2>
          <p className="text-zinc-400 text-base leading-relaxed">
            Every binder gets a shareable, view-only Showcase. Drop the link in a Discord server, a subreddit or
            your group chat and it unfurls into a preview of your best cards — no account needed to look. Friends
            can browse, filter and zoom into your snapshots, while your notes, sale targets and account stay
            private. Flip it off any time and the link goes dead instantly.
          </p>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li className="flex items-start gap-2"><span className="text-[#A78BFA] mt-0.5">✓</span> View-only — nobody can touch your collection</li>
            <li className="flex items-start gap-2"><span className="text-[#A78BFA] mt-0.5">✓</span> Social preview shows your top cards automatically</li>
            <li className="flex items-start gap-2"><span className="text-[#A78BFA] mt-0.5">✓</span> Revoke access with one switch</li>
          </ul>
          {!user && (
            <Button data-testid="landing-showcase-login-button" onClick={loginWithGoogle} variant="outline" className="rounded-full border-white/20 hover:bg-white/5 px-6">
              Start your binder
            </Button>
          )}
        </div>
      </section>

      <section data-testid="landing-ebay-section" className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#A78BFA]/40 bg-[#A78BFA]/[0.08] px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] animate-pulse" />
            <span className="text-[10px] uppercase tracking-widest text-[#A78BFA] font-semibold">Now live: eBay integration</span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold">
            List straight to eBay <span className="text-[#A78BFA]">from your binder</span>
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed">
            Connect your eBay seller account once. Then pick cards, hit <span className="text-white font-medium">List on eBay</span>, and CardArk pushes fully-formed drafts into your Seller Hub — title, description, condition, aspects, category and image all pre-filled. You review and publish, we never post anything live for you.
          </p>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li className="flex items-start gap-2.5">
              <ShoppingCart className="w-4 h-4 text-[#A78BFA] mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Live &quot;For Sale&quot; on every card</span> — see the cheapest active eBay listing next to CardArk&apos;s valuation, so your asking price is always benchmarked to the real market.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <UploadCloud className="w-4 h-4 text-[#A78BFA] mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Bulk draft creation</span> — select up to 50 cards at once, pick a marketplace (UK, US, DE, AU, CA), and every draft lands in your Seller Hub in seconds.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <TrendingUp className="w-4 h-4 text-[#A78BFA] mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Pricing you can trust</span> — CardArk&apos;s trimmed-median values, cross-checked against active eBay listings, so you never underprice a chase card.</span>
            </li>
          </ul>
          {user ? (
            <Link to="/list-ebay">
              <Button data-testid="landing-ebay-cta" className="bg-[#A78BFA] hover:bg-[#8B6FE8] text-black font-semibold rounded-full px-6 mt-2">
                List cards on eBay <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          ) : (
            <Button
              data-testid="landing-ebay-cta"
              onClick={() => window.dispatchEvent(new Event("open-google-auth"))}
              className="bg-[#A78BFA] hover:bg-[#8B6FE8] text-black font-semibold rounded-full px-6 mt-2"
            >
              Start scanning to list <ExternalLink className="ml-2 w-4 h-4" />
            </Button>
          )}
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#111113] p-5 max-w-md mx-auto w-full">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
            <ShoppingCart className="w-3 h-3 text-[#A78BFA]" /> Live from eBay UK
          </div>
          <div className="space-y-3">
            <div className="p-3 rounded-lg border border-[#A78BFA]/20 bg-[#A78BFA]/[0.04]">
              <p className="text-[10px] text-zinc-500 mb-1">Cheapest active listing · Charizard, Base Set #4</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">PSA 8 · seller topratedcards (99.7%)</span>
                <span className="text-base font-mono-plex text-emerald-300 font-semibold">£1,204.99</span>
              </div>
            </div>
            <div className="p-3 rounded-lg border border-white/10 bg-black/30 flex items-center justify-between">
              <span className="text-sm text-zinc-300">CardArk median (last 12 sales)</span>
              <span className="text-base font-mono-plex text-[#A78BFA] font-semibold">£1,325.00</span>
            </div>
            <p className="text-[11px] text-zinc-600 leading-relaxed pt-1">
              Cheapest active is <span className="text-zinc-400">9% below</span> the CardArk median — a fair market to list into today.
            </p>
          </div>
        </div>
      </section>

      <section data-testid="landing-community-section" className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold">A community that keeps you collecting</h2>
          <p className="text-zinc-400 text-base leading-relaxed">
            Your binder doesn't live in a vacuum. Explore other collectors' public binders, search every card in
            the community, and battle for a spot on the collector leaderboard — with rivalry alerts the moment
            someone overtakes you.
          </p>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li className="flex items-start gap-2.5">
              <Trophy className="w-4 h-4 text-[#F59E0B] mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Collector leaderboard</span> — ranked by collection value, card count or top card. Get emailed when a rival overtakes you.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Heart className="w-4 h-4 text-pink-400 mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Chase Deck</span> — heart any card you're hunting. The second it appears in someone's binder, you get pinged.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Receipt className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Trade pings</span> — spotted a card you want? Ping the owner and swap contact details when they accept.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Gift className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
              <span><span className="text-white font-medium">Invite &amp; earn</span> — share your invite link and you both get 10 free scan credits.</span>
            </li>
          </ul>
          <Link to="/community">
            <Button data-testid="landing-community-cta" variant="outline" className="rounded-full border-white/20 hover:bg-white/5 px-6 mt-2">
              Browse the community <Trophy className="ml-2 w-4 h-4 text-[#F59E0B]" />
            </Button>
          </Link>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#111113] p-5 max-w-md mx-auto w-full">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
            <Trophy className="w-3 h-3 text-[#F59E0B]" /> Collector leaderboard
          </div>
          <div className="space-y-2">
            {[
              { rank: "1", name: "Charizard Cave", value: "$4,812", badge: "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/40" },
              { rank: "2", name: "Master Ball Mia", value: "$3,204", badge: "bg-zinc-400/15 text-zinc-300 border-zinc-400/30" },
              { rank: "3", name: "Shiny Grove", value: "$2,977", badge: "bg-orange-700/20 text-orange-300 border-orange-600/40" },
              { rank: "7", name: "You?", value: "Scan to climb", badge: "bg-[#7C3AED]/20 text-[#A78BFA] border-[#7C3AED]/40", you: true },
            ].map((r) => (
              <div key={r.rank} className={`flex items-center gap-3 rounded-xl border p-3 ${r.you ? "border-[#7C3AED]/40 bg-[#7C3AED]/5" : "border-white/5 bg-black/30"}`}>
                <span className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center text-xs font-bold ${r.badge}`}>{r.rank}</span>
                <span className="text-sm font-medium text-white flex-1 truncate">{r.name}</span>
                <span className={`text-xs font-mono-plex ${r.you ? "text-[#A78BFA]" : "text-[#F59E0B]"} font-semibold`}>{r.value}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-600 mt-3">↑ rivalry alerts land in your inbox when someone overtakes you</p>
        </div>
      </section>

      <section data-testid="landing-faq-section" className="max-w-3xl mx-auto px-6 py-24 border-t border-white/10">
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-10">Frequently asked questions</h2>
        <div className="space-y-4">
          {LANDING_FAQS.map((f) => (
            <div key={f.q} data-testid="landing-faq-item" className="border border-white/10 bg-[#111113] rounded-xl p-5">
              <h3 className="font-display font-semibold text-base md:text-lg mb-2 text-white">{f.q}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/guides" data-testid="landing-faq-guides-link" className="text-sm font-semibold text-[#A78BFA] hover:text-white transition-colors">
            Read our free guides on valuing, grading and selling cards →
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-500">
          <span>CardArk — know your collection, backed by real last-sold prices.</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/guides" data-testid="footer-guides-link" className="hover:text-white transition-colors">Guides</Link>
            <Link to="/guides/how-much-is-my-pokemon-card-worth" data-testid="footer-value-guide-link" className="hover:text-white transition-colors">Card values</Link>
            <Link to="/guides/how-to-tell-if-a-pokemon-card-is-rare" data-testid="footer-rare-guide-link" className="hover:text-white transition-colors">Spotting rares</Link>
            <Link to="/guides/how-to-spot-fake-pokemon-cards" data-testid="footer-fake-guide-link" className="hover:text-white transition-colors">Spotting fakes</Link>
            <Link to="/guides/psa-vs-cgc-vs-beckett-grading" data-testid="footer-grading-guide-link" className="hover:text-white transition-colors">Grading</Link>
            <Link to="/guides/card-condition-pricing-guide" data-testid="footer-condition-guide-link" className="hover:text-white transition-colors">Condition &amp; pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
